import * as React from 'react';
import {LegacyModal} from '@flexe/ui-components';
import {format as formatDate} from 'date-fns';
import {ReactNode} from 'react';
import {OrderLine} from '../OutboundOrdersInterfaces';
import {getLotExpirationDateForDisplay, getOrderLineStatusDisplayLabel} from '../helpers/OrderLines';
import {timestampFormat} from '../helpers/Constants';
import {OMSFeatureFlags} from '../../ecommerce-orders/OrdersAppInterfaces';
import {Packaging} from '../../../shared/CommonInterfaces';

interface Props {
  orderLine: OrderLine;
  featureFlags: OMSFeatureFlags;
}

const buildCalculatedShipByDateCells = (featureFlags: OMSFeatureFlags) => {
  if (!featureFlags.showCalculatedShipByDate) {
    return [undefined, undefined];
  }

  return ['Calculated Ship by Date', 'TODO'];
};

const buildHeaderTableRow = (
  label1: string,
  value1: string | ReactNode,
  label2?: string,
  value2?: string | ReactNode
) => {
  return (
    <tr className="tight borderless">
      <td className="header-25pct">
        <b>{label1}:</b>
      </td>
      <td className="header-25pct">{value1}</td>
      <td className="header-25pct">{label2 && <b>{label2}:</b>}</td>
      <td className="header-remainder">{value2}</td>
    </tr>
  );
};

const buildHeaderTable = (orderLine: OrderLine, featureFlags: OMSFeatureFlags) => {
  const textClassName = 'subtle-hover';
  const lineStatus = getOrderLineStatusDisplayLabel(orderLine);
  const flexeOrderId = <div className={textClassName}>{orderLine.orderId}</div>;
  const flexeLineId = <div className={textClassName}>{orderLine.id}</div>;
  const externalLineId = <div className={textClassName}>{orderLine.externalId}</div>;
  const createdAt = <div className={textClassName}>{formatDate(orderLine.createdAt, timestampFormat)}</div>;
  const [calculatedShipByDateLabel, calculatedShipByDate] = buildCalculatedShipByDateCells(featureFlags);

  return (
    <table className="table outbound-orders-shared-table">
      <tbody>
        {buildHeaderTableRow('Status', lineStatus, 'Flexe Order ID', flexeOrderId)}
        {buildHeaderTableRow('Flexe Line ID', flexeLineId, 'Created', createdAt)}
        {buildHeaderTableRow('External Line ID', externalLineId, calculatedShipByDateLabel, calculatedShipByDate)}
      </tbody>
    </table>
  );
};

interface ContentsValue {
  rawValue?: string | number;
  defaultValue?: string;
}

const buildSimpleContentsValueCell = (value: ContentsValue) => {
  const displayValue = value.rawValue || value.defaultValue;

  return (
    <div>
      <span>{displayValue}</span>
    </div>
  );
};

const buildQuantityCell = (orderLine: OrderLine) => {
  const uom = orderLine.unitOfMeasure ? orderLine.unitOfMeasure : Packaging.each;

  return (
    <div>
      <span>
        {orderLine.quantity} {uom}
      </span>
    </div>
  );
};

const buildItemPriceCell = (orderLine: OrderLine) => {
  const displayValue =
    orderLine.itemPrice || orderLine.itemCurrency ? (
      <>
        {orderLine.itemPrice} {orderLine.itemCurrency}
      </>
    ) : (
      <i>not specified</i>
    );

  return (
    <div>
      <span>{displayValue}</span>
    </div>
  );
};

const buildContentsListRow = (label: string, value: string | ReactNode) => {
  return (
    <tr className="tight borderless">
      <td className="header-10pct">
        <b>{label}:</b>
      </td>
      <td className="header-25pct">
        <div className="subtle-hover">{value}</div>
      </td>
    </tr>
  );
};

const buildContentsList = (orderLine: OrderLine, showLotInfo: boolean) => {
  const reservationCell = buildSimpleContentsValueCell({
    rawValue: orderLine.reservationId,
    defaultValue: 'not specified'
  });
  const skuCell = buildSimpleContentsValueCell({rawValue: orderLine.sku});
  const lotCodeCell = buildSimpleContentsValueCell({rawValue: orderLine.inventoryLotControl?.lotCode});
  const expirationDateCell = buildSimpleContentsValueCell({
    rawValue: getLotExpirationDateForDisplay(orderLine.inventoryLotControl)
  });
  const quantityCell = buildQuantityCell(orderLine);
  const itemPriceCell = buildItemPriceCell(orderLine);

  return (
    <table className="table outbound-orders-shared-table">
      <tbody>
        {buildContentsListRow('Requested Reservation', reservationCell)}
        {buildContentsListRow('SKU', skuCell)}
        {showLotInfo && buildContentsListRow('Lot Code', lotCodeCell)}
        {showLotInfo && buildContentsListRow('Expiration Date', expirationDateCell)}
        {buildContentsListRow('Quantity', quantityCell)}
        {buildContentsListRow('Item Price per Unit & Currency', itemPriceCell)}
      </tbody>
    </table>
  );
};

const OrderLineDetailModalV2: React.FC<Props> = (props: Props) => {
  const orderLine = props.orderLine;

  // TODO replace all references of placeholder with functions that work
  //  This is just used for iterative development
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const placeholderFunction = (_: any) => {
    return;
  };
  const placeholderBoolean = false;

  return (
    <LegacyModal
      id="order-line-detail-modal"
      title={`Order Line #${orderLine.id}`}
      show={true}
      size="medium"
      toggleModal={placeholderFunction}
      disableClose={false}
    >
      <div>
        <div className="row modal-detail-header">
          <div className="col-xs-6 no-padding">
            <h5>
              <i className="fa fa-list-ul"></i>
              &nbsp; Line Details
            </h5>
          </div>
        </div>
        <div className="row modal-detail-header">{buildHeaderTable(orderLine, props.featureFlags)}</div>
        <div className="row space-above-lg">
          <h4>Contents</h4>
          {buildContentsList(orderLine, placeholderBoolean)}
        </div>
      </div>
    </LegacyModal>
  );
};
export default OrderLineDetailModalV2;
